import { useState, useEffect } from "react";
import { walletApi } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Wallet } from "@phosphor-icons/react";
import { format } from "date-fns";

const SOURCE_LABEL = {
  mpesa: "M-Pesa top-up",
  auto_pay: "Auto-pay",
  manual_pay: "Manual pay",
  sweep: "Sweep arrears",
  admin: "Adjustment",
};

export default function WalletStatementPage() {
  const [txns, setTxns] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([walletApi.me(), walletApi.transactions(200)])
      .then(([w, t]) => {
        setBalance(w.data?.balance || 0);
        setTxns(t.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="wallet-statement-page">
      <header className="sticky top-0 z-40 bg-primary px-4 py-3 flex items-center gap-3 text-primary-foreground">
        <Link to="/dashboard/profile" className="p-1 -ml-1"><ArrowLeft className="w-5 h-5" /></Link>
        <h1 className="font-heading text-lg font-semibold flex-1">Wallet Statement</h1>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200 rounded-xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">
              <Wallet className="w-5 h-5" weight="fill" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Current balance</p>
              <p className="font-heading text-2xl font-bold text-emerald-700" data-testid="wallet-page-balance">KES {Math.round(balance).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
            <p className="font-heading font-semibold text-sm">Transactions ({txns.length})</p>
          </div>
          {loading ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : txns.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">No wallet activity yet. Top up to get started.</div>
          ) : (
            <div className="divide-y divide-border">
              {txns.map(t => {
                const credit = t.type === "topup";
                return (
                  <div key={t.id} className="px-5 py-3 flex items-start justify-between gap-3" data-testid={`wallet-txn-${t.id}`}>
                    <div className="flex items-start gap-2 min-w-0">
                      <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${credit ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
                        {credit ? <ArrowDown className="w-3.5 h-3.5" weight="bold" /> : <ArrowUp className="w-3.5 h-3.5" weight="bold" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{t.note || SOURCE_LABEL[t.source] || t.type}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {SOURCE_LABEL[t.source] || t.source}
                          {t.reference && <> · <span className="font-mono">{t.reference}</span></>}
                          {t.createdAt && <> · {format(new Date(t.createdAt), "dd MMM yyyy HH:mm")}</>}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold font-body ${credit ? "text-emerald-700" : "text-orange-700"}`}>{credit ? "+" : "−"} KES {Math.round(t.amount).toLocaleString()}</p>
                      <p className="text-[11px] text-muted-foreground">Balance: KES {Math.round(t.balanceAfter || 0).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
